//Copyright (c) 2024 Greener Power Solutions.
//This file is part of Greener Power Solutions' software,
//and may not be altered, copied, redistributed, or sold
//without specific permission.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseQueryResult } from 'react-query';
import { ReactQueryConfig } from 'hooks/hooks.types';
import { useAxiosQuery } from 'hooks/useAxiosQuery';
import { PaginatedResponse } from 'types';

type UseInfiniteAxiosQueryProps<T> = {
  key: string[];
  path: string;
  perPage?: number;
  reactQueryConfig?: ReactQueryConfig<PaginatedResponse<T>>;
  initialPage?: string;
  accumulateData?: boolean;
  page?: number;
  onPageChange?: (page: number) => void;
};

export function useInfiniteAxiosQuery<T>({
  key,
  path,
  perPage = 10,
  reactQueryConfig = {},
  accumulateData = true,
  page: controlledPage,
  onPageChange,
}: UseInfiniteAxiosQueryProps<T>) {
  const [uncontrolledPage, setUncontrolledPage] = useState(1);
  const page = controlledPage ?? uncontrolledPage;

  const setPage = useCallback(
    (newPage: number | ((prev: number) => number)) => {
      if (controlledPage && onPageChange) {
        onPageChange(typeof newPage === 'function' ? newPage(controlledPage) : newPage);
      } else {
        setUncontrolledPage(newPage);
      }
    },
    [controlledPage, onPageChange]
  );

  //inject the pagination params into whatever query url is already constructed from useQueryparams (other filters)
  const queryUrlWithPagination = useMemo(() => {
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}per_page=${perPage}&page=${page}`;
  }, [path, perPage, page]);

  // State to maintain combined data if accumulation is enabled
  const [accumulatedData, setAccumulatedData] = useState<T[]>([]);
  const { data, isSuccess, isError, isFetching, isLoading, ...rest } = useAxiosQuery<PaginatedResponse<T>>(
    [...key, page.toString(), perPage.toString()],
    queryUrlWithPagination,
    {
      ...reactQueryConfig,
      keepPreviousData: true,
      staleTime: 0, //we want this infinite fetching to be as reactive as possible especially wrt changes in queryKey
    }
  );

  useEffect(() => {
    if (isSuccess && data && accumulateData && data.currentPage === page) {
      setAccumulatedData((prevData) => (page === 1 ? data.data : [...prevData, ...data.data]));
    }
  }, [data, isSuccess, accumulateData, page]);

  const hasNextPage = useMemo(() => data && data?.currentPage < data?.totalPages, [data]);

  const goToNextPage = useCallback(() => {
    if (!hasNextPage) {
      return;
    }

    setPage((currPage) => currPage + 1);
  }, [setPage, hasNextPage]);

  const goToPreviousPage = useCallback(() => setPage((currPage) => Math.max(currPage - 1, 1)), [setPage]);
  const resetPage = useCallback(() => {
    setPage(1);
  }, [setPage]);

  return {
    //Return accumulated data for infinite scroll and also data per page for traditional pagination.
    data: { accumulatedData, perPageData: [...(data?.data ?? [])] },
    currentPage: data?.currentPage,
    totalPages: data?.totalPages,
    totalRecords: data?.totalRecords,
    hasNextPage,
    isSuccess,
    isError,
    isLoading,
    isFetching,
    goToNextPage,
    goToPreviousPage,
    resetPage,
    setPage,
    ...(rest as Partial<UseQueryResult>),
  };
}
